import React from 'react';
import { Button } from './Primitives';

/**
 * The last thing between a thrown error and a white screen.
 *
 * The reference implementation had no boundary at all: when a lazily-loaded
 * dialog suspended outside one, React unmounted the entire tree and the team
 * were left looking at nothing, with no way back but a manual reload. That must
 * not be the default behaviour of a product other companies depend on, so this
 * is in place from the first session — before there is much for it to catch.
 *
 * What it deliberately does *not* do: show the error text. A stack trace can
 * carry a customer's name, an account number or a row of somebody's ledger, and
 * this screen is the one a stranger sees. The person gets a reference they can
 * quote; the detail goes to `onError`, which the app points at its server log.
 */

export interface ErrorBoundaryProps {
    children: React.ReactNode;
    /** Reported somewhere durable. Never rendered. */
    onError?: (error: Error, info: React.ErrorInfo, reference: string) => void;
    /** What the "start again" button does. Defaults to a reload. */
    onReset?: () => void;
    /** Offered alongside, when there is a session to leave. */
    onSignOut?: () => void;
    /** Replaces the default screen entirely, for a boundary around one panel. */
    fallback?: (state: { reference: string; reset: () => void }) => React.ReactNode;
}

interface ErrorBoundaryState {
    failed: boolean;
    reference: string;
}

const newReference = (): string =>
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2)
    ).replace(/-/g, '').slice(0, 8).toUpperCase();

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    override state: ErrorBoundaryState = { failed: false, reference: '' };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { failed: true, reference: newReference() };
    }

    override componentDidCatch(error: Error, info: React.ErrorInfo): void {
        this.props.onError?.(error, info, this.state.reference);
    }

    private readonly reset = (): void => {
        this.setState({ failed: false, reference: '' });
        if (this.props.onReset) this.props.onReset();
        else if (typeof window !== 'undefined') window.location.reload();
    };

    override render(): React.ReactNode {
        if (!this.state.failed) return this.props.children;
        if (this.props.fallback) {
            return this.props.fallback({ reference: this.state.reference, reset: this.reset });
        }

        return (
            <div role="alert" className="min-h-[60vh] flex items-center justify-center p-6">
                <div className="bg-card rounded-[16px] shadow-e1 max-w-md w-full px-7 py-8 text-center">
                    <h1 className="text-[20px] font-bold text-label tracking-[-0.02em]">
                        Something went wrong
                    </h1>
                    <p className="text-[14px] text-label-2 mt-3 leading-relaxed">
                        Nothing you had saved is affected. Starting again usually clears it.
                    </p>
                    <p className="text-[12.5px] text-label-3 mt-4">
                        If it keeps happening, quote reference{' '}
                        <span className="num font-semibold text-label-2">{this.state.reference}</span>.
                    </p>
                    <div className="flex items-center justify-center gap-2 mt-7">
                        <Button variant="primary" onClick={this.reset}>Start again</Button>
                        {this.props.onSignOut && (
                            <Button variant="quiet" onClick={this.props.onSignOut}>Sign out</Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
